import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'
import logo from '@/assets/GrackleDocs_BirdOnly.png'
import { markFreshLogin } from '@/lib/loginSignal'

export default function AuthCallback() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('Processing authentication...')

  /*
   * Read the fragment ONCE, at first render, not inside the effect.
   *
   * The effect below clears the fragment from the address bar so the tokens leave browser
   * history. React's StrictMode runs effects twice in development, and when the parsing lived in
   * the effect the second run found the fragment already gone, concluded the login had failed,
   * and scheduled a redirect to /login?error=oauth_failed half a second after the successful one
   * to "/". That bounce remounted the whole app straight after login, which among other things
   * closed the welcome-back summary the moment it opened.
   *
   * Capturing the values here makes the effect idempotent: run twice, it stores the same tokens
   * twice and schedules the same redirect, and its cleanup cancels the first timer.
   */
  const [auth] = useState(() => {
    const params = new URLSearchParams(window.location.hash.substring(1))
    return {
      accessToken: params.get('access_token'),
      refreshToken: params.get('refresh_token'),
      userParam: params.get('user'),
    }
  })

  useEffect(() => {
    // Tokens arrive in the URL fragment rather than query parameters: fragments are never sent
    // to servers, never logged, and never appear in Referer headers.
    const { accessToken, refreshToken, userParam } = auth
    let timer: ReturnType<typeof setTimeout>

    if (accessToken && refreshToken && userParam) {
      try {
        const user = JSON.parse(userParam)

        // Store tokens and user info in localStorage
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', refreshToken)
        localStorage.setItem('user', JSON.stringify(user))
        localStorage.setItem('gracklecrm_current_user_id', user.id)
        // A real login, not a reload: lets the welcome-back summary open once we're in.
        markFreshLogin(user.id)

        // Clear the hash from the URL to remove tokens from browser history
        window.history.replaceState(null, '', window.location.pathname)

        setStatus('success')
        setMessage(`Welcome, ${user.name}!`)

        // HR-only users (no CRM access) land on the HR workspace, not the CRM
        // dashboard they can't see.
        const dest = user.user_type === 'Employee' ? '/hr' : '/'
        timer = setTimeout(() => {
          navigate(dest)
        }, 1500)
      } catch (error) {
        console.error('Error parsing user data:', error)
        setStatus('error')
        setMessage('Failed to process authentication data.')
        timer = setTimeout(() => {
          navigate('/login?error=oauth_failed')
        }, 2000)
      }
    } else {
      setStatus('error')
      setMessage('Missing authentication data.')
      timer = setTimeout(() => {
        navigate('/login?error=oauth_failed')
      }, 2000)
    }

    return () => clearTimeout(timer)
  }, [auth, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <img src={logo} alt="Grackle CRM" className="h-16 w-16 object-contain" />
          </div>
          <div className="text-center space-y-2">
            <CardTitle className="text-2xl font-bold">
              {status === 'loading' && 'Authenticating...'}
              {status === 'success' && 'Success!'}
              {status === 'error' && 'Error'}
            </CardTitle>
            <CardDescription>{message}</CardDescription>
          </div>
        </CardHeader>

        <CardContent className="flex justify-center py-8">
          {status === 'loading' && <Loader2 className="h-12 w-12 animate-spin text-primary" />}
          {status === 'success' && <CheckCircle className="h-12 w-12 text-[#2E7D5B]" />}
          {status === 'error' && <XCircle className="h-12 w-12 text-[#941052]" />}
        </CardContent>
      </Card>
    </div>
  )
}
